import React, {ChangeEvent} from 'react';
import {MetadataPropertyType} from "../../api/entities/replancity_MetadataProperty";
import {formatNumber} from "../../utils/utils";
import * as TextField from "./TextField/TextField";
import * as ColorPickerField from "./ColorPickerField/ColorPickerField";
import * as CheckboxField from './CheckboxField/CheckboxField';
import {Option} from "./SelectBox/SelectBox";
import * as RoadSegmentField from "./RoadSegmentField/RoadSegmentField";
import * as DrawGeometryField from "./DrawGeometryField/DrawGeometryField";
import PublicTransitVehicleTypeField from "./PublicTransitVehicleTypeField/PublicTransitVehicleTypeField";
import PublicTransitFareField from "./PublicTransitFareField/PublicTransitFareField";
import * as SelectBox from "./SelectBox/SelectBox";
import {EntityFormMetadataProperty} from "../Entities/EntityForm/EntityForm";
import {Row} from "./Form/Form";
import {useTranslation} from "react-i18next";


type Props = {
    value: any;
    toShowField: boolean;
    required: boolean;
    metadata: EntityFormMetadataProperty;
    onChange?: (event: ChangeEvent<HTMLElement>) => void;
    autoFocus?: boolean;
}

const FieldCreator = React.memo(React.forwardRef(({
                                                      value,
                                                      toShowField,
                                                      required,
                                                      metadata,
                                                      onChange,
                                                      autoFocus
                                                  }: Props, ref: React.ForwardedRef<HTMLElement>) => {
    const {t} = useTranslation();

    const {
        id,
        name,
        options,
        printedName,
        type,
        validationRegex,
        saveAndUpdateOnChange
    } = metadata;

    //TODO probably type check and consequent options generation should be done in some classes/utils
    const mappedSelectOptions: Option[] = options.map(({id, propertyName, printedName}) => ({
        value: type === 'Enum' ? propertyName : id,
        caption: printedName ?? propertyName
    }));

    const alertLabel = saveAndUpdateOnChange ? t('transit-lines.form-save-on-field-update-msg') : '';

    const getField = (
        selectOptions: Option[],
        label: string,
        type: MetadataPropertyType,
        value: any,
        onChange?: (event: ChangeEvent<HTMLElement>) => void,
        alertLabel?: string,
        autoFocus?: boolean,
        validationRegex?: string
    ) => {
        switch (type) {
            case 'String':
            case 'Project':
            case 'UUID':
                return <TextField.Labeled
                    name={name}
                    label={label}
                    alertLabel={alertLabel}
                    type="text"
                    required={required}
                    value={value}
                    onChange={onChange}
                    ref={ref as any}
                    autoFocus={autoFocus}
                    validationRegex={validationRegex}
                />
            case 'LocalTime':
            case 'DEPARTURE':
            case 'INTERVAL':
                return <TextField.Labeled
                    name={name}
                    label={label}
                    alertLabel={alertLabel}
                    type="time"
                    required={required}
                    value={value}
                    step="1"
                    onChange={onChange}
                    ref={ref as any}
                    testId={name}
                    validationRegex={validationRegex}
                />
            case 'Integer':
            case 'Double':
                return <TextField.Labeled
                    name={name}
                    label={label}
                    alertLabel={alertLabel}
                    type="text"
                    required={required}
                    value={formatNumber(value)}
                    onChange={onChange}
                    ref={ref as any}
                    validationRegex={validationRegex}
                />
            case "Boolean":
                return <CheckboxField.Labeled
                    name={name}
                    checked={!!value && value !== 'false'}
                    alertLabel={alertLabel}
                    label={label}
                    onChange={onChange}
                    required={required}
                />
            case "Color":
                return <ColorPickerField.Labeled
                    name={name}
                    value={value as string}
                    alertLabel={alertLabel}
                    label={label}
                    onChange={onChange}
                    required={required}
                />
            case 'Enum':
                return <SelectBox.Labeled
                    label={label}
                    alertLabel={alertLabel}
                    name={name}
                    options={mappedSelectOptions}
                    value={value}
                    required={required}
                    onChange={onChange}
                    ref={ref}
                />
            case 'PublicTransitFareOptions':
                return <PublicTransitFareField
                    label={label}
                    alertLabel={alertLabel}
                    name={name}
                    options={selectOptions}
                    value={value}
                    required={required}
                    onChange={onChange}
                    ref={ref as any}
                />
            case 'PublicTransitVehicleType':
                return <PublicTransitVehicleTypeField
                    label={label}
                    alertLabel={alertLabel}
                    name={name}
                    options={selectOptions}
                    value={value}
                    required={required}
                    onChange={onChange}
                />
            case 'Point':
            case 'LineString':
            case 'Polygon':
                return <DrawGeometryField.Labeled
                    label={label}
                    alertLabel={alertLabel}
                    name={name}
                    value={value}
                    drawMode={type}
                    required={required}
                    onChange={onChange}
                />
            case 'RoadInfrastructureSegment':
                return <RoadSegmentField.Labeled
                    label={label}
                    alertLabel={alertLabel}
                    name={name}
                    value={value}
                    required={required}
                    onChange={onChange}
                />
            default:
                return <TextField.Labeled
                    name={name}
                    label={label}
                    alertLabel={alertLabel}
                    type="text"
                    required={required}
                    value={value}
                    onChange={onChange}
                    ref={ref as any}
                    autoFocus={autoFocus}
                    validationRegex={validationRegex}
                />
        }
    }

    if (!toShowField) {
        return null;
    }

    return (
        <Row key={id}>
            {getField(mappedSelectOptions, printedName, type, value, onChange, alertLabel, autoFocus, validationRegex)}
        </Row>
    )
}));

export default FieldCreator;